import { OPTIONS_MULTIPLIER, RISK_FREE_RATE } from "@/lib/constants";
import { callPrice, putPrice } from "@/lib/options/greeks";
import { payoffAtExpiry } from "@/lib/options/payoff";
import {
  applyLegDeltas,
  applyLegOverrides,
  filterDisabledLegs,
} from "@/lib/strategy/leg-deltas";
import { getRegistryEntry } from "@/lib/strategy/registry";
import type { OptionLeg } from "@/types/options";
import type { StrategyLayerKind } from "@/types/strategy";

const STANDARD_BACK_LEG_REMAINING_T = 30 / 365;
const STANDARD_SIGMA = 0.22;
const STANDARD_SINGLE_LEG_PREMIUM_RATIO = 0.25;

export type StrategyPreviewPoint = {
  price: number;
  pnl: number;
};

function optionIntrinsic(leg: OptionLeg, price: number) {
  return leg.type === "call"
    ? Math.max(0, price - leg.strike)
    : Math.max(0, leg.strike - price);
}

function optionTimeValue(leg: OptionLeg, price: number) {
  const pricingParams = {
    S: price,
    K: leg.strike,
    T: STANDARD_BACK_LEG_REMAINING_T,
    r: RISK_FREE_RATE,
    sigma: STANDARD_SIGMA,
  };
  return leg.type === "call"
    ? callPrice(pricingParams)
    : putPrice(pricingParams);
}

function centerAcrossZero(points: StrategyPreviewPoint[]) {
  const pnls = points.map((point) => point.pnl);
  const min = Math.min(...pnls);
  const max = Math.max(...pnls);
  if (!Number.isFinite(min) || !Number.isFinite(max) || max - min < 1e-6) {
    return points;
  }

  const offset = -(min + max) / 2;
  return points.map((point) => ({ ...point, pnl: point.pnl + offset }));
}

/**
 * Builds a fixed teaching example for strategy-picking previews.
 *
 * This intentionally does not use the live ticker or live option premium. The
 * preview communicates payoff shape. Multi-leg examples are centered across
 * zero, while single Call / Put examples keep a stable illustrative premium.
 */
export function buildStandardizedStrategyPreview({
  kind,
  params: paramOverrides,
  spot,
  strikeStep,
  range,
  steps,
}: {
  kind: StrategyLayerKind;
  params?: Record<string, number>;
  spot: number;
  strikeStep: number;
  range: number;
  steps: number;
}): StrategyPreviewPoint[] {
  const minPrice = spot - range;
  const priceStep = (range * 2) / steps;
  const prices = Array.from(
    { length: steps + 1 },
    (_, index) => minPrice + priceStep * index,
  );

  if (kind === "custom") {
    return prices.map((price) => ({ price, pnl: 0 }));
  }

  const entry = getRegistryEntry(kind);
  const defaults = entry.defaultParams(spot, strikeStep);
  const params = paramOverrides ? { ...defaults, ...paramOverrides } : defaults;
  const legs = filterDisabledLegs(
    applyLegOverrides(
      applyLegDeltas(entry.toLegs(spot, params, strikeStep), params),
      params,
    ),
    params,
  );

  if (kind === "single_call" || kind === "single_put") {
    const leg = legs[0];
    if (!leg) return prices.map((price) => ({ price, pnl: 0 }));

    // Single-leg previews use a stable illustrative premium instead of the
    // universal zero-centering used by multi-leg shapes. This keeps the
    // limited premium loss visibly smaller than the directional upside.
    const premium = range * STANDARD_SINGLE_LEG_PREMIUM_RATIO;
    const netCredit = premium * leg.quantity * (leg.side === "short" ? 1 : -1);
    return prices.map((price) => ({
      price,
      pnl: payoffAtExpiry(legs, netCredit, price),
    }));
  }

  const rawPoints = prices.map((price) => {
    if (kind === "covered_call") {
      const coveredContracts = Math.max(
        1,
        ...legs
          .filter((leg) => leg.type === "call" && leg.side === "short")
          .map((leg) => leg.quantity),
      );
      const stockPnl = (price - spot) * OPTIONS_MULTIPLIER * coveredContracts;
      return {
        price,
        pnl: stockPnl + payoffAtExpiry(legs, 0, price),
      };
    }

    if (kind === "time_spread" || kind === "double_time_spread") {
      const pnl = legs.reduce((sum, leg) => {
        // At the front expiry, short front-month legs are intrinsic while the
        // long back-month legs retain time value.
        const value =
          leg.side === "short"
            ? optionIntrinsic(leg, price)
            : optionTimeValue(leg, price);
        const direction = leg.side === "long" ? 1 : -1;
        return sum + value * direction * leg.quantity * OPTIONS_MULTIPLIER;
      }, 0);
      return { price, pnl };
    }

    return { price, pnl: payoffAtExpiry(legs, 0, price) };
  });

  return centerAcrossZero(rawPoints);
}
